import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDeliveryNote } from './queryKeys';

type UpdateTemplateResponse = {
  code: number;
  id: UUID;
  requestId: string;
};

type UpdateTemplatePayload = {
  name: string;
  defaultTemplate?: boolean;
  companyId?: UUID;
  template_id: UUID;
  body: Record<string, any>;
};

/**
 * Updates a template.
 *
 * @param {UpdateTemplatePayload} templateData - An object containing the template details to be updated.
 * @returns {Promise<UpdateTemplateResponse>} - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/DLN-Template/operation/update_dln_template_dln_template_put
 */
export const updateTemplate = async (templateData: UpdateTemplatePayload) => {
  const { body, ...params } = templateData;
  const response = await vestigasApi
    .put(ENDPOINT.DELIVERY_NOTE.UPDATE_TEMPLATE(), {
      json: body,
      searchParams: params,
    })
    .json<UpdateTemplateResponse>();

  return response;
};

/**
 * Custom hook for updating a template using mutations.
 *
 * @returns {Object} An object containing the mutation function and other properties.
 */
export const useMutationUpdateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateData: UpdateTemplatePayload) =>
      updateTemplate(templateData),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeysDeliveryNote.getTemplates(),
      });
    },
  });
};
